import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Scrollbar } from 'swiper/modules';

// Import Swiper styles
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/scrollbar';

const PlayIcon = () => (
  <svg width="48" height="48" viewBox="0 0 48 48" fill="white">
    <path d="M16 10v28l22-14z"/>
  </svg>
);

const DeleteIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
    <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
  </svg>
);

export const VideoCarousel = ({
  videos = [],
  onVideoClick,
  onVideoDelete,
  isEditing = false,
  showUploadSlots = false,
  onVideoUpload,
  videoLoading = false,
  maxVideos = 6
}) => {
  const handleVideoClick = (video) => {
    if (!isEditing && onVideoClick) {
      onVideoClick(video);
    }
  };

  const handleVideoDelete = (e, videoId) => {
    e.stopPropagation();
    if (onVideoDelete) {
      onVideoDelete(videoId);
    }
  };

  const handleFileChange = (e) => {
    if (onVideoUpload) {
      onVideoUpload(e);
    }
  };

  // Calculate upload slots if needed
  const uploadSlotsCount = showUploadSlots ? Math.max(0, maxVideos - videos.length) : 0;

  return (
    <div className="video-carousel-container relative">
      <style>{`
        .video-carousel-container .swiper-button-next,
        .video-carousel-container .swiper-button-prev {
          color: #ffffff;
           font-size: 8px;
          // background: rgba(34, 211, 238, 0.85);
          // width: 32px;
          // height: 32px;
          // border-radius: 50%;
          // transition: all 0.3s ease;
          // box-shadow: 0 2px 8px rgba(0, 0, 0, 0.25);
        }

        // .video-carousel-container .swiper-button-next:hover,
        // .video-carousel-container .swiper-button-prev:hover {
        //   background: rgba(34, 211, 238, 1);
        //   transform: scale(1.08);
        //   box-shadow: 0 4px 12px rgba(34, 211, 238, 0.5);
        // }

        // .video-carousel-container .swiper-button-next::after,
        // .video-carousel-container .swiper-button-prev::after {
        //   font-size: 12px;
        //   font-weight: 900;
        //   color: #ffffff;
        // }

        .video-carousel-container .swiper-button-disabled {
          opacity: 0.35;
          cursor: not-allowed;
        }

        /* Responsive sizing for larger screens */
        @media (min-width: 640px) {
          .video-carousel-container .swiper-button-next,
          .video-carousel-container .swiper-button-prev {
            width: 36px;
            height: 36px;
          }

          .video-carousel-container .swiper-button-next::after,
          .video-carousel-container .swiper-button-prev::after {
            font-size: 13px;
          }
        }

        @media (min-width: 768px) {
          .video-carousel-container .swiper-button-next,
          .video-carousel-container .swiper-button-prev {
            width: 40px;
            height: 40px;
          }

          .video-carousel-container .swiper-button-next::after,
          .video-carousel-container .swiper-button-prev::after {
            font-size: 14px;
          }
        }

        @media (min-width: 1024px) {
          .video-carousel-container .swiper-button-next,
          .video-carousel-container .swiper-button-prev {
            width: 44px;
            height: 44px;
          }

          .video-carousel-container .swiper-button-next::after,
          .video-carousel-container .swiper-button-prev::after {
            font-size: 15px;
          }
        }

        .video-carousel-container .swiper-scrollbar {
          background: rgba(100, 116, 139, 0.3);
          height: 6px;
          border-radius: 3px;
          margin-top: 12px;
        }

        .video-carousel-container .swiper-scrollbar-drag {
          background: #22d3ee;
          border-radius: 3px;
          cursor: grab;
        }

        .video-carousel-container .swiper-scrollbar-drag:active {
          cursor: grabbing;
        }
      `}</style>

      <Swiper
        modules={[Navigation, Scrollbar]}
        spaceBetween={16}
        slidesPerView={2}
        navigation
        scrollbar={{ draggable: true }}
        breakpoints={{
          640: {
            slidesPerView: 3,
            spaceBetween: 16,
          },
          768: {
            slidesPerView: 4,
            spaceBetween: 16,
          },
          1024: {
            slidesPerView: 5,
            spaceBetween: 16,
          },
          1280: {
            slidesPerView: 6,
            spaceBetween: 16,
          },
        }}
        className="pb-8"
      >
        {/* Existing Videos */}
        {videos.map((video, index) => (
          <SwiperSlide key={video._id || index}>
            <div
              onClick={() => handleVideoClick(video)}
              className={`relative aspect-[9/16] bg-jp-section rounded-lg overflow-hidden group ${
                isEditing ? 'cursor-default' : 'cursor-pointer'
              }`}
            >
              {/* Delete Button - Only show in edit mode */}
              {isEditing && onVideoDelete && (
                <button
                  onClick={(e) => handleVideoDelete(e, video._id)}
                  disabled={videoLoading}
                  className="absolute top-2 right-2 z-10 p-1.5 bg-red-500 hover:bg-red-600 rounded-full transition-opacity disabled:opacity-50"
                >
                  <DeleteIcon />
                </button>
              )}

              {/* Video Thumbnail */}
              <div className="absolute inset-0 bg-jp-section">
                {video.thumbnailUrl ? (
                  <img
                    src={video.thumbnailUrl}
                    alt={video.title || 'Video'}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                ) : (video.videoUrl || video.url || video.src) ? (
                  <video
                    src={video.videoUrl || video.url || video.src}
                    className="w-full h-full object-cover pointer-events-none"
                    preload="metadata"
                    muted
                    playsInline
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="text-center">
                      <PlayIcon />
                      <p className="text-jp-ink text-xs mt-2">{video.title || 'Video'}</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Play Overlay on Hover - Only show when not editing */}
              {!isEditing && (
                <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                  <PlayIcon />
                </div>
              )}

              {/* Video Title */}
              {video.title && (
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-2">
                  <p className="text-white text-xs truncate">{video.title}</p>
                </div>
              )}
            </div>
          </SwiperSlide>
        ))}

        {/* Upload Slots */}
        {showUploadSlots && !videoLoading && [...Array(uploadSlotsCount)].map((_, index) => (
          <SwiperSlide key={`upload-${index}`}>
            <label
              className={`relative aspect-[9/16] bg-jp-section rounded-lg border-2 border-dashed border-jp-line flex items-center justify-center transition-all group ${
                isEditing ? 'cursor-pointer hover:border-jp-accent/60 hover:bg-jp-line' : 'cursor-not-allowed opacity-60'
              }`}
            >
              <input
                type="file"
                accept="video/*"
                onChange={handleFileChange}
                className="hidden"
                disabled={!isEditing || videoLoading}
              />
              <div className="text-center">
                <svg
                  className={`w-12 h-12 mx-auto mb-2 transition-colors ${
                    isEditing ? 'text-jp-muted/80 group-hover:text-jp-accent' : 'text-jp-muted/80'
                  }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                </svg>
                <p
                  className={`text-xs transition-colors ${
                    isEditing ? 'text-jp-muted/80 group-hover:text-jp-accent' : 'text-jp-muted/80'
                  }`}
                >
                  Upload Video
                </p>
              </div>
            </label>
          </SwiperSlide>
        ))}

        {/* Loading State */}
        {videoLoading && showUploadSlots && (
          <SwiperSlide>
            <div className="relative aspect-[9/16] bg-jp-section rounded-lg border-2 border-dashed border-cyan-500/50 flex items-center justify-center">
              <div className="w-12 h-12 border-4 border-jp-accent border-t-transparent rounded-full animate-spin"></div>
            </div>
          </SwiperSlide>
        )}
      </Swiper>
    </div>
  );
};

export default VideoCarousel;
